
import { useState, useEffect } from 'react';
import { Button, Avatar, Dialog } from 'antd-mobile';
import {
    Users,
    Trash2,
    Send,
} from 'lucide-react';
import liff from '@line/liff';
import { useStore } from '../store';
import { api } from '../api';
import './StaffPage.css';

interface StaffMember {
    id: number;
    line_user_id: string;
    display_name: string | null;
    picture_url: string | null;
    created_at: string;
}

export default function StaffPage() {
    const dbUser = useStore((s) => s.dbUser);
    const showToast = useStore((s) => s.showToast);

    const [staff, setStaff] = useState<StaffMember[]>([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);

    useEffect(() => {
        fetchStaff();
    }, [dbUser]);

    const fetchStaff = async () => {
        if (!dbUser) return;
        try {
            const res = await api.getStaffList(dbUser.line_user_id);
            setStaff(res.staff || []);
        } catch (error) {
            console.error('Error fetching staff:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleInviteViaLine = async () => {
        if (!dbUser) return;
        setGenerating(true);
        try {
            const res = await api.createInvite(dbUser.line_user_id);
            try {
                const result = await liff.shareTargetPicker([
                    {
                        type: 'flex',
                        altText: `${dbUser.display_name || 'เจ้าของร้าน'} เชิญคุณเข้าร่วมร้าน ${res.shop_name}`,
                        contents: res.flex_message as any,
                    }
                ]);

                if (result) {
                    showToast('ส่งคำเชิญสำเร็จ!');
                } else {
                    showToast('ยกเลิกการส่ง');
                }
            } catch (shareError: any) {
                console.error('shareTargetPicker error:', shareError);
                const shareText = `${dbUser.display_name || 'เจ้าของร้าน'} เชิญคุณเข้าร่วมร้าน!\n\nกดลิงก์เพื่อเข้าร่วม:\n${res.invite_url}`;
                const lineShareUrl = `https://line.me/R/share?text=${encodeURIComponent(shareText)}`;
                window.location.href = lineShareUrl;
            }
        } catch (error: any) {
            console.error('Error inviting:', error);
            showToast(error.message || 'ไม่สามารถส่งคำเชิญได้');
        } finally {
            setGenerating(false);
        }
    };

    const handleRemoveStaff = async (staffMember: StaffMember) => {
        const result = await Dialog.confirm({
            title: 'ลบ Staff',
            content: `ต้องการลบ "${staffMember.display_name || 'Staff'}" ออกจากร้าน?`,
            confirmText: 'ลบ',
            cancelText: 'ยกเลิก',
        });

        if (!result || !dbUser) return;

        try {
            await api.removeStaff(dbUser.line_user_id, staffMember.line_user_id);
            showToast('ลบ Staff สำเร็จ');
            fetchStaff();
        } catch (error: any) {
            showToast(error.message || 'เกิดข้อผิดพลาด');
        }
    };

    return (
        <div className="page staff-page-minimal">
            <div className="simple-header">
                <h1>จัดการทีม</h1>
            </div>

            <div className="staff-content-minimal">
                {loading ? (
                    <div className="minimal-loading">กำลังโหลด...</div>
                ) : staff.length === 0 ? (
                    <div className="minimal-empty">
                        <img
                            src="/invite.png"
                            alt="Invite Staff"
                            style={{ width: '160px', height: '160px', objectFit: 'contain', marginBottom: '10px' }}
                        />
                        <h3>ยังไม่มี Staff</h3>
                        <p>เชิญ Staff ผ่าน LINE Chat ได้เลย</p>
                        <Button
                            color="primary"
                            onClick={handleInviteViaLine}
                            loading={generating}
                            shape="rounded"
                            className="minimal-invite-btn"
                        >
                            <Send size={18} style={{ marginRight: 8 }} />
                            ส่งคำเชิญผ่าน LINE
                        </Button>
                    </div>
                ) : (
                    <>
                        <div className="minimal-staff-list">
                            {staff.map((member) => (
                                <div key={member.id} className="minimal-staff-item">
                                    <Avatar
                                        src={member.picture_url || ''}
                                        className="minimal-avatar"
                                    />
                                    <div className="minimal-info">
                                        <div className="minimal-name">
                                            {member.display_name || 'ไม่ระบุชื่อ'}
                                        </div>
                                        <div className="minimal-role">Staff</div>
                                    </div>
                                    <button
                                        className="minimal-remove-btn"
                                        onClick={() => handleRemoveStaff(member)}
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            ))}
                        </div>

                        <div className="bottom-invite-area">
                            <Button
                                block
                                color="primary"
                                onClick={handleInviteViaLine}
                                loading={generating}
                                shape="rounded"
                                size='large'
                                style={{ fontWeight: 600 }}
                            >
                                <Send size={20} style={{ marginRight: 8 }} />
                                เพิ่ม Staff คนใหม่
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
